import { Link } from 'gatsby';
import React, { FormEvent, FunctionComponent, useEffect, useLayoutEffect, useState } from 'react';
import { useScreenSize } from '../../hooks/useScreenSize';
import { Common, PagePath, SanityNavLink } from '../../types/common';
import { formatLink } from '../../utils/formatLink';
import { GlassIcon, HamburguerIcon, LogoIcon, XIcon } from '../Icons';
import NavLink from '../NavLink';
import SocialLink from '../SocialLink';
import LogoAnimated from './LogoAnimated';
import NavItem from './NavItem';
import './styles.scss';

export type HeaderProps = {
  data: Common;
  isHome?: boolean;
  headerBg?: string;
}

function determineIfIsNavLink(item: SanityNavLink | PagePath): item is SanityNavLink {
  return !!(item as SanityNavLink).typeLink;
}

const AccessibilityNav = () => {
  return (
    <ul className="accessibility-nav">
      <li>
        <a className="skip-link" href="#mainContent">
          skip to content
        </a>
      </li>
      <li>
        <a className="skip-link" href="#footer">
          skip to footer
        </a>
      </li>
    </ul>
  );
};

const Header: FunctionComponent<HeaderProps> = ({ data, isHome, headerBg }) => {
  const headerData = data.sanityHeader;
  const settingsData = data.sanitySiteSettings;

  if (!headerData) {
    return null;
  }

  const { isMobile } = useScreenSize();
  const [isTransparent, setIsTransparent] = useState(true);
  const [showNavigation, setShowNavigation] = useState(false);
  const [email, setEmail] = useState('');
  const [error, setError] = useState(false);
  const [newsletterStage, setNewsletterStage] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) return setIsTransparent(false);
      setIsTransparent(true);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  async function handleSubscribe(event: FormEvent) {
    event.preventDefault();
    if (newsletterStage === 0) {
      setNewsletterStage(1);
    }
    if (newsletterStage === 1) {
      try {
        await fetch('https://nnri6tifkbko7mjei7lhqmg2wy0ftstf.lambda-url.us-east-1.on.aws/', {
          method: 'POST',
          body: JSON.stringify({
            email,
            type: "newsletter"
          })
        }).then(response => {
          if (!response.ok) throw Error()
        });
      } catch {
        setError(true);
      } finally {
        setNewsletterStage(2);
      }
    }
  }

  const Navigation = () => {
    return (
      <ul className='navigation'>
        {headerData.navItems.map((item, index) => <NavItem item={item} key={index} />)}
      </ul>
    )
  }

  const AdditionalItems = () => {
    return (
      <ul className='additional-items'>
        {headerData.additionalItems?.map((item) => {
          if (determineIfIsNavLink(item)) {
            return (
              <li key={item._key}>
                <NavLink data={item} />
              </li>
            );
          }
          return (
            <li key={item._id}>
              <Link to={formatLink(item)} onClick={() => setShowNavigation(false)} className='search'>
                <GlassIcon size={20} />
                <span>search</span>
              </Link>
            </li>
          );
        })}
      </ul>
    )
  }

  return (
    <header className={`header ${showNavigation ? 'active' : ''} ${headerBg} ${(isHome && isTransparent && !showNavigation) ? 'transparent' : ''}`}>
      <AccessibilityNav />
      {!isMobile ? (
        <div className='wrapper desktop'>
          <Link to='/' className='logo'>
            <LogoIcon size={160} />
          </Link>
          <Navigation />
          <AdditionalItems />
        </div>
      ) : (
        <div className='header-mobile'>
          <div className='header-mobile-container'>
            <button
              type='button'
              onClick={() => setShowNavigation(prevState => !prevState)}
              className='hamburguer-button'
              title={showNavigation ? 'Close menu' : 'Open menu'}
            >
              {showNavigation ? (
                <XIcon />
              ) : (
                <HamburguerIcon />
              )}
            </button>
            <Link to='/' onClick={() => setShowNavigation(false)}>
              <LogoAnimated open={showNavigation} />
            </Link>
            <AdditionalItems />
          </div>
          <div className='header-mobile-content' tabIndex={showNavigation ? 1 : -1}>
            <Navigation />
            {/* <form
              className='header-subscribe'
              onSubmit={handleSubscribe}
            >
              {newsletterStage < 2 && (
                <>
                  <p>Get the latest peanut news, recipes, and health tips delivered straight to your inbox.</p>
                  <div className={newsletterStage === 0 ? 'input-wrapper hidden' : 'input-wrapper'}>
                    <input
                      placeholder='Write your e-mail'
                      type='email'
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required={newsletterStage === 1}

                    />
                  </div>
                  <button className='primary-button-small' disabled={!email && newsletterStage === 1}>Subscribe</button>
                </>
              )}
              {newsletterStage === 2 && (
                error ? (
                  <div>
                    <h3 style={{ color: '#0B5C98' }}>Error to subscribe!</h3>
                    <p>Try again later.</p>
                  </div>
                ) : (
                  <div>
                    <h3>Subscribed!</h3>
                    <p>Thank you for subscribing to our newsletter!</p>
                    <p>You'll now receive the latest news and updates straight to your inbox. We appreciate your interest and look forward to staying in touch.</p>
                  </div>
                )
              )}
            </form> */}
            <div className='header-footer'>
              {settingsData?.social && (
                <div className='header-footer-social'>
                  {settingsData.social.map(link => (
                    <SocialLink key={link._key} data={link} />
                  ))}
                </div>
              )}
              <p>{settingsData?.websiteTrademark}</p>
            </div>
          </div>
        </div>
      )}
    </header>
  )
};

export default Header;
